import {useEffect, useMemo, useRef} from "react";
import * as FRAGS from "@thatopen/fragments";
import * as THREE from "three";
import {toast} from "react-toastify";
import {ImgLoader} from "@treegram/loaders-img";
import {FragmentLoader} from "@treegram/loaders-fragment";
import {_3drtgmLoader} from "@treegram/loaders-3drtgm";
import {_3dtgmLoader} from "@treegram/loaders-3dtgm";
import {SaveTool} from "@treegram/tools-save";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useStoreyMeasuresContext from "../context/measures/use-storey-measures-context.ts";
import useStoreyIndicatorsContext from "../context/indicators/use-storey-indicators-context.ts";
import useViewerItemsVisibilityContext
    from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";
import ENDPOINTS from "@/services/endpoints.ts";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import FileType from "@/models/enums/file-type.ts";
import FileRecord from "@/pages/takeoff/context/project-measure/models/file-record.ts";
import FragmentRecord from "@/pages/takeoff/context/project-measure/models/fragment-record.ts";
import {ViewerLoader} from "@/hooks/viewer/use-viewer.ts";

export const useStoreyMeasuresLoaders = (): ViewerLoader[] => {
    const { project } = useProjectContext();
    const { buildingsFileData } = useProjectMeasureContext();
    const { storey, storeyFilesRecord, storeyFragsRecord, measures } = useStoreyMeasuresContext();
    const { isLoading: is3DFilesPathsLoading, storeyPathsData } = useStoreyIndicatorsContext();
    const { initialiseVisibleItems, subscribeVisibilityChanged } = useViewerItemsVisibilityContext();

    const sitePositionRef = useRef<THREE.Vector3 | undefined>(undefined);
    const visibleItems = useRef<string[] | undefined>(undefined);
    const viewerGeometries = useRef<string[]>([]);
    const initialInit = useRef<boolean>();

    const flatStoreyFragsData = useMemo(() => storeyFragsRecord.flatMap((f) => f), [storeyFragsRecord]);

    useEffect(() => {
        if(measures.length === 0 || initialInit.current) return;
        viewerGeometries.current = measures.flatMap(m => m.geometryRepresentations.map(g => g.geometryId));
        initialInit.current = true;
    }, [measures]);

    useEffect(() => {
        const firstFile3D = Array.from(buildingsFileData.values())
            .map(files =>
                files.find(f => f.fileType === FileType.File3D && f.sitePosition != null)
            )
            .find(Boolean);

        if (firstFile3D?.sitePosition) {
            sitePositionRef.current = firstFile3D.sitePosition;
        }
    }, [buildingsFileData]);

    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, FileRecord>>(() => {
        const organisationId = project.projectOrganisation.id;
        const projectId = project.id;

        return {
            key: "plan-pdf-loader",
            factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: storeyFilesRecord,
            fetchUrl: (file) =>
                ENDPOINTS.VIEWER_PDF_PREVIEW(projectId, `${organisationId}/${projectId}/${file.relativePath}`),
            name: (file) => `${crypto.randomUUID()}_${file.name}`,
            getOptions: (file) => {
                let loaderOptions = {};
                if (file.content instanceof KnownScaleContent) {
                    loaderOptions = { format: file.content.format, orientation: file.content.orientation, scale: file.content.scale };
                } else if (file.content instanceof UserDefinedContent) {
                    loaderOptions = { definedScale: file.content.scale };
                }
                return { useCoordinates: false, loaderOptions };
            },
            onItemLoaded: async (ctx, loader, file) => {
                const dimensionsCanvas = loader.getDimensionsCanvas();
                if (dimensionsCanvas) {
                    file.setDimensions(dimensionsCanvas.width, dimensionsCanvas.height);
                    const largestSide = Math.max(dimensionsCanvas.width, dimensionsCanvas.height);
                    if (largestSide > 0) {
                        await ctx.viewer.getCamera().controls.setLookAt(0, largestSide * 0.9, 0, 0, 0, 0);
                    }
                }
            },
            onError: (_ctx, _loader, file) => {
                toast.error(`Erreur lors du chargement des fichiers PDF : ${file.name}`, { autoClose: false });
            },
        };
    }, [project.id, project.projectOrganisation.id, storeyFilesRecord]);

    const fragLoader = useMemo<ViewerLoader<unknown, FragmentLoader, FragmentRecord>>(
        () => ({
            key: "frag-loader",
            factory: (ctx) => new FragmentLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: flatStoreyFragsData,
            fetchUrl: (file) => ENDPOINTS.VIEWER_FILE(file.filePath),
            name: (file) => `${crypto.randomUUID()}_${file.name}`,
            options: {
                useCoordinates: false,
                loaderOptions: {
                    lodMode: FRAGS.LodMode.ALL_GEOMETRY,
                    origin: new THREE.Vector2(0, 0),
                    direction: new THREE.Vector2(1, 0),
                    loadHidden: true,
                },
            },
            onItemLoaded: async (ctx, loader) => {
                const model = loader.getModel();
                if (model) {
                    const saveTool = ctx.getTool(SaveTool);
                    saveTool?.setupOptions({
                        modelId: model.modelId,
                        lodMode: FRAGS.LodMode.ALL_GEOMETRY,
                        clearAfterSave: true,
                    });
                }

                if (visibleItems.current == null || visibleItems.current.length === 0) {
                    visibleItems.current = viewerGeometries.current;
                }

                initialiseVisibleItems(visibleItems.current);
                await ctx.viewer.showItems(visibleItems.current);
            },
            onError: (_ctx, _loader, file) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${file.name}`, { autoClose: false });
            },
        }),
        [flatStoreyFragsData, initialiseVisibleItems],
    );

    const boomer3drtgmLoader = useMemo<ViewerLoader<unknown, _3drtgmLoader, { path: string; name: string }>>(
        () => ({
            key: "boomer-3drtgm-loader",
            factory: (ctx) => new _3drtgmLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: is3DFilesPathsLoading || !storeyPathsData ? [] : storeyPathsData.filter((dto) => dto.path.endsWith(".3drtgm")),
            fetchUrl: (dto) => ENDPOINTS.VIEWER_FILE_BOOMER(dto.path),
            name: (dto) => `${crypto.randomUUID()}_${dto.name}`,
            getOptions: () => {
                let loaderOptions = {
                    lodMode: FRAGS.LodMode.ALL_GEOMETRY,
                    loadHidden: true,
                    origin: new THREE.Vector2(0, 0),
                    direction: new THREE.Vector2(1, 0),
                    altimetry: 0,
                };

                if (storeyFilesRecord.length > 0) {
                    const filePlan = storeyFilesRecord[0];
                    const translation = new THREE.Vector2(-filePlan.origin.position.x, -filePlan.origin.position.y);
                    loaderOptions = {
                        ...loaderOptions,
                        origin: new THREE.Vector2(
                            filePlan.origin.direction.x * translation.x + filePlan.origin.direction.y * translation.y,
                            -filePlan.origin.direction.y * translation.x + filePlan.origin.direction.x * translation.y
                        ),
                        direction: new THREE.Vector2(filePlan.origin.direction.x, -filePlan.origin.direction.y),
                        altimetry: -storey.altimetry,
                    };

                    if (filePlan.sitePosition) {
                        const translationSite = new THREE.Vector2(
                            -(filePlan.origin.position.x + filePlan.sitePosition.x - (sitePositionRef.current?.x ?? 0)),
                            -(filePlan.origin.position.y + filePlan.sitePosition.y - (sitePositionRef.current?.y ?? 0))
                        );
                        loaderOptions.origin = new THREE.Vector2(
                            filePlan.origin.direction.x * translationSite.x + filePlan.origin.direction.y * translationSite.y,
                            -filePlan.origin.direction.y * translationSite.x + filePlan.origin.direction.x * translationSite.y
                        );
                        loaderOptions.altimetry = -(storey.altimetry - (sitePositionRef.current?.z ?? 0));
                    }
                }

                return { useCoordinates: false, loaderOptions };
            },
            onError: (_ctx, _loader, dto) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${dto.name}`, { autoClose: false });
            },
        }),
        [is3DFilesPathsLoading, storeyPathsData, storeyFilesRecord, storey.altimetry],
    );

    const boomer3dtgmLoader = useMemo<ViewerLoader<unknown, _3dtgmLoader, { path: string; name: string }>>(
        () => ({
            key: "boomer-3dtgm-loader",
            factory: (ctx) => new _3dtgmLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: is3DFilesPathsLoading || !storeyPathsData ? [] : storeyPathsData.filter((dto) => !dto.path.endsWith(".3drtgm")),
            fetchUrl: (dto) => ENDPOINTS.VIEWER_FILE_BOOMER(dto.path),
            name: (dto) => `${crypto.randomUUID()}_${dto.name}`,
            getOptions: () => {
                let loaderOptions = {
                    lodMode: FRAGS.LodMode.ALL_GEOMETRY,
                    loadHidden: true,
                    origin: new THREE.Vector2(0, 0),
                    direction: new THREE.Vector2(1, 0),
                    altimetry: 0,
                };

                if (storeyFilesRecord.length > 0) {
                    const filePlan = storeyFilesRecord[0];
                    const translation = new THREE.Vector2(-filePlan.origin.position.x, -filePlan.origin.position.y);
                    loaderOptions = {
                        ...loaderOptions,
                        origin: new THREE.Vector2(
                            filePlan.origin.direction.x * translation.x + filePlan.origin.direction.y * translation.y,
                            -filePlan.origin.direction.y * translation.x + filePlan.origin.direction.x * translation.y
                        ),
                        direction: new THREE.Vector2(filePlan.origin.direction.x, -filePlan.origin.direction.y),
                        altimetry: -storey.altimetry,
                    };

                    if (filePlan.sitePosition) {
                        const translationSite = new THREE.Vector2(
                            -(filePlan.origin.position.x + filePlan.sitePosition.x - (sitePositionRef.current?.x ?? 0)),
                            -(filePlan.origin.position.y + filePlan.sitePosition.y - (sitePositionRef.current?.y ?? 0))
                        );
                        loaderOptions.origin = new THREE.Vector2(
                            filePlan.origin.direction.x * translationSite.x + filePlan.origin.direction.y * translationSite.y,
                            -filePlan.origin.direction.y * translationSite.x + filePlan.origin.direction.x * translationSite.y
                        );
                        loaderOptions.altimetry = -(storey.altimetry - (sitePositionRef.current?.z ?? 0));
                    }
                }

                return { useCoordinates: false, loaderOptions };
            },
            onError: (_ctx, _loader, dto) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${dto.name}`, { autoClose: false });
            },
        }),
        [is3DFilesPathsLoading, storeyPathsData, storeyFilesRecord, storey.altimetry],
    );

    useEffect(() => subscribeVisibilityChanged(async ({ action, items }) => {
        if (action === "add") {
            visibleItems.current = [...(visibleItems.current ?? []), ...items];
        }

        if (action === "remove") {
            visibleItems.current = visibleItems.current?.filter(item => !items.includes(item));
        }
    }), [subscribeVisibilityChanged]);


    return useMemo<ViewerLoader[]>(
        () => [planPdfLoader, fragLoader, boomer3drtgmLoader, boomer3dtgmLoader],
        [planPdfLoader, fragLoader, boomer3drtgmLoader, boomer3dtgmLoader],
    );
};

export default useStoreyMeasuresLoaders;
